import {ApolloClient, gql, InMemoryCache, NormalizedCacheObject} from '@apollo/client';
import {SubmitHandler} from 'react-hook-form';
import {UTMParams} from 'src/hooks/useUTMParams';
import {trackGoogleAdsConversion} from 'src/utils/googleAds';

import {SubmitLeadFormInputType, SubmitLeadFormResponseType} from './leadFormSubmission.types';

const graphqlGatewayUri = process.env.GATSBY_GRAPHQL_GATEWAY_URI;
if (!graphqlGatewayUri) {
  throw new Error('Could not find GATSBY_GRAPHQL_GATEWAY_URI. Please check your config / environment variables.');
}

/**
 * Second-order function which returns a handler function for lead form submission via `react-hook-form`. It grabs lead form query parameters out of the url, as well.
 *
 * Separate from the Lead form component to keep file sizes small.
 *
 * @param setSubmittedSuccessfully: the success `setState` handler from the @type {LeadForm} component
 * @param setHasSubmissionError: the error `setState` handler from the @type {LeadForm} component
 *
 * @returns a react-hook-form SubmitHandler, ready to be used as the `onSubmit` of a form
 *
 * @see https://react-hook-form.com/get-started#TypeScript
 */
export const getLeadFormSubmissionHandler = (
  setShowForm: React.Dispatch<React.SetStateAction<boolean>>,
  setSubmittedSuccessfully: React.Dispatch<React.SetStateAction<boolean>>,
  setHasSubmissionError: React.Dispatch<React.SetStateAction<boolean>>,
  utmParams: UTMParams | null,
): SubmitHandler<SubmitLeadFormInputType['input']> => {
  let client: ApolloClient<NormalizedCacheObject> | undefined;

  if (typeof window !== 'undefined') {
    client = new ApolloClient({
      cache: new InMemoryCache(),
      uri: graphqlGatewayUri,
    });
  }

  // build submission handler using the state handlers passed in as arguments
  const onLeadFormSubmit: SubmitHandler<SubmitLeadFormInputType['input']> = async rawFieldValues => {
    const input = {
      // UTM params
      //The ID below is the production campaignId for Apply Form
      campaignId: utmParams?.campaignId || (window.location.pathname === '/apply' ? '701PU00000CY4n3YAD' : null),
      leadSource: utmParams?.leadSource ?? null,
      sourceType: utmParams?.sourceType ?? null,
      nominatorId: utmParams?.nominatorId ?? null,
      utm: {
        id: utmParams?.utm ?? null,
        campaign: utmParams?.utmCampaign ?? null,
        content: utmParams?.utmContent ?? null,
        medium: utmParams?.utmMedium ?? null,
        source: utmParams?.utmSource ?? null,
      },
      // Inputted form data
      formData: rawFieldValues,
    };

    // Hide form during submission
    setShowForm(false);

    try {
      const response = await client?.mutate<SubmitLeadFormResponseType, SubmitLeadFormInputType>({
        mutation: gql`
          mutation SubmitLeadForm($input: SubmitLeadFormInput!) {
            submitLeadForm(input: $input) {
              success
            }
          }
        `,
        variables: {
          input,
        },
      });

      if (response?.data?.submitLeadForm.success) {
        setSubmittedSuccessfully(true);
        setHasSubmissionError(false);
        trackGoogleAdsConversion(window.location);
        return;
      }
      // else
      setHasSubmissionError(true);
      setShowForm(true); // Show form again when there are errors
      return;
    } catch (err) {
      // TODO: Log to datadog
      setHasSubmissionError(true);
      setShowForm(true); // Show form again when there are errors
    }
  };
  // return the handler function we just built
  return onLeadFormSubmit;
};
